import { ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

const VARIANT_STYLES = {
  light: {
    panel: 'border-slate-200 bg-white shadow-[0_0_40px_-10px_rgba(0,0,0,0.12)]',
    toggle: 'border-b border-slate-100 bg-[#FAFBFC] text-[#475569] hover:bg-[#F1F5F9] hover:text-[#4338CA]',
    header: 'border-b border-slate-100',
    title: 'text-sm font-bold text-[#1E293B]',
    description: 'text-[11px] leading-snug text-[#64748B]',
    icon: 'text-[#6366F1]',
    collapsedIcon: 'text-[#6366F1]',
    collapsedLabel: 'text-[10px] font-semibold text-[#64748B]',
    empty: 'rounded-xl border border-dashed border-slate-200 bg-slate-50/80 p-4 text-center text-xs text-[#64748B]',
    footer: 'text-[10px] text-slate-500 border-t border-slate-100',
  },
  dark: {
    panel: 'border-amber-500/20 bg-slate-950/95 shadow-[0_0_40px_-10px_rgba(0,0,0,0.45)]',
    toggle: 'border-b border-amber-500/15 bg-black/60 text-slate-400 hover:bg-amber-500/10 hover:text-amber-300',
    header: 'border-b border-amber-500/15',
    title: 'text-sm font-bold text-amber-200',
    description: 'text-[11px] leading-snug text-slate-500',
    icon: 'text-amber-400',
    collapsedIcon: 'text-amber-500/80',
    collapsedLabel: 'text-[10px] font-semibold text-slate-500',
    empty: 'rounded-sm border border-dashed border-slate-700 bg-slate-900/50 p-4 text-center text-xs text-slate-500',
    footer: 'text-[10px] text-slate-600 border-t border-slate-800',
  },
};

/**
 * Fixed right foldable history panel — matches dialogue flow on /dialogue/create.
 */
export function FlowHistoryPanel({
  open,
  onToggle,
  title,
  description = '',
  icon: Icon,
  variant = 'light',
  loading = false,
  count = 0,
  onRefresh,
  refreshLoading = false,
  onNew,
  newLabel = 'New',
  emptyMessage = 'Nothing here yet.',
  collapsedLabel,
  testId = 'flow-history-panel',
  children,
  footer,
}) {
  const v = VARIANT_STYLES[variant] || VARIANT_STYLES.light;
  const verticalLabel = collapsedLabel || title;

  return (
    <aside
      className={cn(
        'fixed top-16 right-0 z-40 flex h-[calc(100vh-4rem)] flex-col border-l transition-[width,transform] duration-200 ease-out',
        v.panel,
        open ? 'w-full sm:w-80' : 'w-11',
      )}
      aria-label={title}
      data-testid={testId}
    >
      <button
        type="button"
        onClick={onToggle}
        className={cn('flex h-14 shrink-0 items-center justify-center gap-0 px-1 transition-colors', v.toggle)}
        title={open ? 'Collapse panel' : `Open ${title}`}
        data-testid={`${testId}-toggle`}
      >
        {open ? (
          <ChevronRight className="h-5 w-5 shrink-0" />
        ) : (
          <ChevronLeft className="h-5 w-5 shrink-0" />
        )}
      </button>

      {open ? (
        <>
          <div className={cn('px-3 py-3 shrink-0', v.header)}>
            <div className={cn('flex items-center gap-2', v.title)}>
              {Icon ? <Icon className={cn('h-4 w-4 shrink-0', v.icon)} /> : null}
              {title}
            </div>
            {description ? <p className={cn('mt-1', v.description)}>{description}</p> : null}
            <div className="mt-2 flex flex-col gap-1.5">
              {onNew ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className={cn(
                    'h-8 w-full rounded-sm text-xs',
                    variant === 'dark'
                      ? 'border-amber-500/35 text-amber-300 hover:bg-amber-500/10'
                      : 'rounded-lg text-[#6366F1]',
                  )}
                  onClick={onNew}
                  data-testid={`${testId}-new`}
                >
                  {newLabel}
                </Button>
              ) : null}
              {onRefresh ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className={cn(
                    'h-8 w-full text-xs',
                    variant === 'dark' ? 'text-amber-400/80 hover:bg-amber-500/10' : 'rounded-lg text-[#6366F1]',
                  )}
                  onClick={onRefresh}
                  disabled={refreshLoading}
                  data-testid={`${testId}-refresh`}
                >
                  {refreshLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Refresh list'}
                </Button>
              ) : null}
            </div>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto p-2">
            {loading ? (
              <div className="flex justify-center py-8 text-slate-400">
                <Loader2 className="h-6 w-6 animate-spin" />
              </div>
            ) : count === 0 && !children ? (
              <p className={v.empty}>{emptyMessage}</p>
            ) : (
              children
            )}
          </div>

          {footer ? (
            <p className={cn('shrink-0 px-3 py-2', v.footer)}>{footer}</p>
          ) : null}
        </>
      ) : (
        <div className="flex flex-1 flex-col items-center pt-4 pb-2 px-0">
          {Icon ? <Icon className={cn('h-5 w-5 shrink-0', v.collapsedIcon)} title={title} /> : null}
          {!loading && count > 0 ? (
            <span
              className={cn(
                'mt-2 text-[10px] font-bold tabular-nums',
                variant === 'dark' ? 'text-amber-400' : 'text-[#6366F1]',
              )}
            >
              {count}
            </span>
          ) : null}
          <span
            className={cn('mt-3 block max-w-[2.5rem] text-center leading-tight', v.collapsedLabel)}
            style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}
          >
            {verticalLabel}
          </span>
        </div>
      )}
    </aside>
  );
}

export function flowHistoryPanelPaddingClass(open) {
  return open ? 'sm:pr-80' : '';
}

const STORAGE_PREFIX = 'flow_history_panel_open_';

export function readFlowHistoryPanelOpen(key, defaultOpen = true) {
  try {
    const v = localStorage.getItem(`${STORAGE_PREFIX}${key}`);
    if (v !== null) return v === 'true';
  } catch {
    /* ignore */
  }
  if (typeof window !== 'undefined' && window.innerWidth < 640) return false;
  return defaultOpen;
}

export function writeFlowHistoryPanelOpen(key, open) {
  try {
    localStorage.setItem(`${STORAGE_PREFIX}${key}`, open ? 'true' : 'false');
  } catch {
    /* ignore */
  }
}
